import { default as Modal } from './AbstractModal'
import { clamp, map } from "../utils/maths"
import gsap from 'gsap'

const BREAKPOINT_SMALL = 699

export default class extends Modal {
    /**
     * The CSS class name to apply to `<html>` to mark the modal as open.
     *
     * @var {string}
     */
    activeClass = 'has-menu-open'

    constructor(m) {
        super(m)

        // UI
        this.$container  = this.$('container')[0]
        this.$scrollable = this.$('scrollable')[0]
        this.$overlay    = this.$('overlay')[0]
        this.$swipeArea  = this.$('swipeArea')[0]
        this.$mainLinks  = Array.from(this.$('mainLink'))

        // Binding
        this.onResizeEndBind    = this.onResizeEnd.bind(this)
        this.onDragStartBind    = this.onDragStart.bind(this)
        this.onDragEndBind      = this.onDragEnd.bind(this)
        this.onDragBind         = this.onDrag.bind(this)
        this.updateSwipeRafBind = this.updateSwipeRaf.bind(this)
        this.onLinkClickBind    = this.onLinkClick.bind(this)

        // Drag
        this.dragState = {
            isSwipeRafPlaying: false,
            isDragAvailable: false,
            isDragging: false,
            isExpanded: false,
            isOpen: false
        }

        this.drag = {
            oldY: 0,
            y: 0,
            startY: 0,
            smoothY: 0,
            threshold: 150,
        }

        this.dragStep = {
            openY: 0,
            closeY: 0
        }
    }

    ///////////////
    // Events
    ///////////////
    bindEvents() {
        if (this.isTouchDevice) {
            window.addEventListener('resizeEnd', this.onResizeEndBind)
    
            this.$swipeArea.addEventListener('touchstart', this.onDragStartBind, { passive: true })
            this.$swipeArea.addEventListener('touchend',   this.onDragEndBind, { passive: true })
            this.$swipeArea.addEventListener('touchleave', this.onDragEndBind, { passive: true })
            this.$swipeArea.addEventListener('touchmove',  this.onDragBind, { passive: true })
        }

    }

    unbindEvents() {
        if (this.isTouchDevice) {
            window.removeEventListener('resizeEnd', this.onResizeEndBind)
    
            this.$swipeArea.removeEventListener('touchstart', this.onDragStartBind, { passive: true })
            this.$swipeArea.removeEventListener('touchend',   this.onDragEndBind, { passive: true })
            this.$swipeArea.removeEventListener('touchleave', this.onDragEndBind, { passive: true })
            this.$swipeArea.removeEventListener('touchmove',  this.onDragBind, { passive: true })
        }
    }

    ///////////////
    // Abstract Modal callbacks
    ///////////////
    onInit() {
        this.isTouchDevice = window.matchMedia('(pointer:coarse)').matches && window.matchMedia(`(max-width:${BREAKPOINT_SMALL}px)`).matches

        this.bindEvents()

        if (this.isTouchDevice) {
            this.computeDragMetrics()
        }
    }

    onDestroy() {
        this.unbindEvents()
    }

    onClose() {
        this.call('start', null, 'Scroll')

        if (this.isTouchDevice) {
            this.closeSwipe()
        }
    }

    onOpen(args) {
        this.call('stop', null, 'Scroll')

        if (this.isTouchDevice) {
            this.openSwipe()
        }
    }

    ///////////////
    // Swipe Callback
    ///////////////
    onResizeEnd() {
        this.computeDragMetrics()
    }

    onDragStart(event) {
        if (!this.dragState.isDragAvailable || !this.dragState.isOpen) return
        
        this.dragState.isDragging = true
        
        this.playSwipeRaf()

        this.pointerEvent = event.type !== 'mousedown' ? (event.touches[0] || event.changedTouches[0]) : event

        this.drag.oldY = this.drag.y

        this.drag.startY = this.pointerEvent.clientY
    }

    onDrag(event) {
        if(!this.dragState.isDragging) return

        this.pointerEvent = event.type !== 'mousemove' ? (event.touches[0] || event.changedTouches[0]) : event

        this.drag.y = this.drag.oldY + (this.pointerEvent.clientY - this.drag.startY)
        
        this.drag.y = clamp(this.dragStep.openY, this.dragStep.closeY, this.drag.y)
    }

    onDragEnd(event) {
        if(!this.dragState.isDragging) return

        this.dragState.isDragging = false

        this.dragDistY = this.pointerEvent.clientY - this.drag.startY
        
        if (!this.dragState.isExpanded && this.dragDistY > this.drag.threshold) {
            this.closeSwipe()
        }
        else if (this.dragState.isExpanded && this.dragDistY > this.drag.threshold) {
            this.closeSwipe()
        }
        else if (this.dragState.isExpanded && this.dragDistY < this.drag.threshold) {
            this.expandSwipe()
        }
        else if (!this.dragState.isExpanded && this.dragDistY < this.drag.threshold) {
            this.expandSwipe()
        }
    }

    onCloseAnimationEnded() {
        this.close?.()
    }

    onLinkClick(e) {
        // Remove all current classes
        this.$mainLinks.forEach(($link) => {
            $link.classList.remove('is-current')
        })

        // Check is selected link is main
        const $link = e.target
        const isMainLink = this.$mainLinks.includes($link)

        // If $link is main, add current class
        if (isMainLink) $link.classList.add('is-current')
    }

    updateCurrentItem() {
        // Get current pathname
        const currentURL = window.location.href

        let index = 0 
        while (index < this.$mainLinks.length) {
            const $link = this.$mainLinks[index]

            // Remove current class
            $link.classList.remove('is-current')

            // Get main link href
            const linkUrl = $link.getAttribute('href')

            // Add current class
            if (currentURL == linkUrl) $link.classList.add('is-current')

            index++
        }
    }

    ///////////////
    // Calc
    ///////////////
    computeDragMetrics() {
        const containerHeight = this.$container.offsetHeight

        // Common metrics
        this.dragStep.closeY = containerHeight

        // Resize metrics
        if (!this.dragState.isExpanded) {
            this.$container.style.transform = `translate3d(0, ${containerHeight}px, 0)`;
            this.drag.y = this.drag.smoothY = containerHeight
        }
    }

    ///////////////
    // Swipe Menu Steps
    ///////////////
    openSwipe() {
        if (!this.isTouchDevice || !window.isMobile || this.dragState.isOpen ) return

        // Set state values
        this.dragState.isDragAvailable = true
        this.dragState.isOpen = true

        // Start raf
        this.playSwipeRaf()
        
        // Set expanded state
        this.expandSwipe()

        // Reset scroll inside menu
        this.$scrollable.scrollTop = 0
    }

    expandSwipe() {
        if (!this.isTouchDevice || !window.isMobile) return

        // Set state
        this.dragState.isExpanded = true

        // Anime
        this.drag.y = this.dragStep.openY
    }
    
    closeSwipe() {
        if (!this.dragState.isOpen) return

        // Set state
        this.dragState.isDragAvailable = false
        this.dragState.isDragging = false
        this.dragState.isExpanded = false
        this.dragState.isOpen = false

        // Anime
        this.drag.y = this.dragStep.closeY

        // Just in case close() method
        // was triggered by overlay click
        this.playSwipeRaf()
    }

    ///////////////
    // Animations
    ///////////////
    updateSwipeBackground(y, minY, maxY) {
        this.$overlay.style.opacity = map(y, minY, maxY, 0, 1)
    }
    
    updateSwipeTransform(y) {
        this.$container.style.transform = `translate3d(0, ${y}px, 0)`;
    }

    ///////////////
    // Render
    ///////////////
    playSwipeRaf() {
        if (this.dragState.isSwipeRafPlaying) return
        this.dragState.isSwipeRafPlaying = true        
        gsap.ticker.add(this.updateSwipeRafBind)
    }

    stopSwipeRaf() {
        if (!this.dragState.isSwipeRafPlaying) return
        this.dragState.isSwipeRafPlaying = false
        gsap.ticker.remove(this.updateSwipeRafBind)
    }

    updateSwipeRaf() {
        // Smooth drag value
        // ===============================
        this.drag.smoothY += (this.drag.y - this.drag.smoothY) * 0.2;
        this.drag.smoothY = clamp(this.dragStep.openY + .5, this.dragStep.closeY, this.drag.smoothY)

        // Apply transform and opacity
        // ===============================
        this.updateSwipeTransform(this.drag.smoothY)
        this.updateSwipeBackground(this.drag.smoothY, this.dragStep.closeY, this.dragStep.openY)

        // Check when we should stop raf
        // ===============================

        // Open is complete
        if (this.dragState.isOpen && !this.dragState.isDragging && this.drag.smoothY <= this.dragStep.openY + .5) {
            this.stopSwipeRaf()
        }

        // Close is complete
        if (!this.dragState.isOpen && this.drag.smoothY > this.dragStep.closeY - .5) {
            this.stopSwipeRaf()
            this.onCloseAnimationEnded()
        }
    }
}